import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useIntl } from 'react-intl'

import { Box, Link, Typography } from '@mui/material'
import { Button } from '@rent/ui'
import { useCountdownTimer } from '@rent/ui/hooks'
import { LayoutWithHeader } from 'components/layouts'
import { NotReadyComponent } from 'components/shared'
import { ConsultantData, ExternalLinks, RouteQueryParam } from 'consts'
import { useThemeConfigContext } from 'context'
import { useRouteQuery } from 'hooks'

import { useGetPaymentInfoExtended } from '../api'

type SummaryInfo = {
  title: string
  content: any
}

const PaymentCallback = () => {
  const { formatMessage, locale } = useIntl()
  const routerQuery = useRouteQuery()
  const orderId = routerQuery.get(RouteQueryParam.PAYMENT_ORDER_ID)

  const { setTheme } = useThemeConfigContext()
  const [summaryInfo, setSummaryInfo] = useState<SummaryInfo | null>(null)

  const {
    data: pData,
    isLoading: pIsFetching,
    isSuccess: pIsSuccess,
    isError: pIsError,
    error: pError,
  } = useGetPaymentInfoExtended(orderId ?? '', !!orderId)

  const loading = pIsFetching

  const handleRedirect = () => {
    window.location.replace(pData?.returnURL ?? ExternalLinks.WESUB)
  }

  const { remainingSeconds } = useCountdownTimer({
    minutes: 0,
    seconds: 15,
    hideSecZeros: true,
    callbackFn: handleRedirect,
  })

  useEffect(() => {
    if (pData) {
      setTheme(pData.vendorShortName)
      setSummaryInfo({
        title: formatMessage({ id: 'notification.dear_customer' }),
        content: formatMessage({ id: 'notification.payment_general_callback' }),
      })
    } else {
      setSummaryInfo({
        title: formatMessage({ id: 'notification.dear_customer' }),
        content: formatMessage({ id: 'notification.payment_general_callback' }),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pData, locale])

  return (
    <>
      <Helmet>
        <title>RentPay - {formatMessage({ id: 'finalization' })}</title>
      </Helmet>
      <NotReadyComponent isLoading={loading} isSuccess={pIsSuccess} isError={pIsError || !orderId} error={pError} redirect={!orderId}>
        <LayoutWithHeader maxWidth="77.2rem" noBgr={!summaryInfo}>
          {summaryInfo && (
            <Box>
              <Typography variant="h2" mb={2.4}>
                {summaryInfo.title}
              </Typography>
              <Typography color="text.secondary" mb={2.4} sx={{ whiteSpace: 'pre-line' }}>
                {summaryInfo.content}
              </Typography>

              <Typography color="text.secondary" variant="body2">
                {formatMessage(
                  { id: 'notification.contact_us_if_any_question' },
                  { email: <Link href={`mailto:${ConsultantData.email}`}>{ConsultantData.email}</Link> },
                )}
              </Typography>

              <Button sx={{ mt: { xs: 3.2, md: 4.6 }, mb: 1.6 }} onClick={handleRedirect}>
                {formatMessage({ id: 'button.go_to_the_store' })}
              </Button>

              <Typography color="text.secondary" variant="body2">
                {formatMessage({ id: 'notification.auto_return_to_the_store' }, { val: remainingSeconds })}
              </Typography>
            </Box>
          )}
        </LayoutWithHeader>
      </NotReadyComponent>
    </>
  )
}

export default PaymentCallback
